import type { Registry as Services } from '@ember/service';
import type AccountSelectorSubscription from 'lumen/models/account-selector-subscription';
import { SUBSCRIPTION_TYPE_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import requestFeatureAccess from 'lumen/utils/hotel-and-market/request-feature-access';
import {
  createAccess,
  type StaffAccessIndicatorProps,
  type StaffAccessSubscription,
} from 'shared-react/lumen';

export type StaffAccessNavigationSubscription = Pick<
  AccountSelectorSubscription,
  'id' | 'isActive' | 'isInternal' | 'salesforceAccountName' | 'type'
>;

export interface StaffAccessNavigation {
  accountSelectorSubscriptions: StaffAccessNavigationSubscription[];
  getFeatures: (subscriptionId: string) => Set<string>;
  hasUnifiedHotelView: boolean;
}

type StaffAccessIntl = Pick<Services['intl'], 't'>;

export interface StaffAccessController {
  ajax: { host: string | undefined };
  hotelAndMarketService: {
    hotel: { id: string | null } | null;
    market: { id: string | null } | null;
    user: { id: string | null };
  };
  intl: StaffAccessIntl;
  isDemoMode: boolean;
  // Only the hotel controller injects navigation; markets have no unified hotel view.
  navigation?: StaffAccessNavigation;
}

function subscriptionName(
  intl: StaffAccessIntl,
  subscription: StaffAccessNavigationSubscription,
): string {
  const typeLabel = intl.t(
    SUBSCRIPTION_TYPE_TRANSLATION_KEYS[subscription.type],
  );

  if (!subscription.salesforceAccountName) {
    return typeLabel;
  }

  return `${typeLabel} - ${subscription.salesforceAccountName}`;
}

function createSubscriptions(
  navigation: StaffAccessNavigation,
  intl: StaffAccessIntl,
): StaffAccessSubscription[] {
  return navigation.accountSelectorSubscriptions.map((subscription) => {
    const subscriptionId = subscription.id!;
    const access = createAccess({
      features: [...navigation.getFeatures(subscriptionId)],
    });

    return {
      deniedFeatures: access.deniedFeatures,
      id: subscriptionId,
      isActive: subscription.isActive !== false,
      isInternal: Boolean(subscription.isInternal),
      name: subscriptionName(intl, subscription),
      restrictedFeatures: access.restrictedFeatures,
    };
  });
}

export default function createStaffAccessProps(
  controller: StaffAccessController,
): StaffAccessIndicatorProps {
  const props: StaffAccessIndicatorProps = {
    isDemoMode: controller.isDemoMode,
    onRequestAccess: (subscriptionId?: string): void => {
      const { hotel, market, user } = controller.hotelAndMarketService;
      const target: Record<string, string> = market
        ? { market_id: market.id! }
        : { subscription_id: subscriptionId ?? hotel!.id! };

      requestFeatureAccess(controller.ajax.host!, {
        ...target,
        user_id: user.id!,
      });
    },
  };

  if (controller.navigation?.hasUnifiedHotelView) {
    props.subscriptions = createSubscriptions(
      controller.navigation,
      controller.intl,
    );
  }

  return props;
}
